import React, {
    ChangeEvent,
    useState,
} from 'react';
import {
    OnKeyboardEvent,
    OnMouseDivEvent,
    OnStringSetState,
} from '@pluto/@types';
import {curry} from 'ramda';
import wrap from 'wraptile';

import TextInput, {TextInputVariant} from './TextInput';

export type OnInputChange = (value: string) => void;

export interface TextInputContainerProps {
    value: string;
    autoFocus: boolean;
    multiline?: boolean;
    rowsMax?: number;
    variant?: TextInputVariant;
    onInputChange: OnInputChange;
    onClick: (event: OnMouseDivEvent) => void;
}

export const onEmitValue = wrap((onInputChange: OnInputChange, value: string, initialValue: string) => {
    if (value === initialValue)
        return;
    
    onInputChange(value);
});

export const onChange = wrap((setInput: OnStringSetState, event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setInput(event.target.value);
});

export const onBlur = wrap((onEmitValue: () => void) => {
    onEmitValue();
});

export const onKeyPress = curry((multiline: boolean, event: OnKeyboardEvent) => {
    if (!multiline && event.key === 'Enter')
        event.target.blur();
});

export default function TextInputContainer(props: TextInputContainerProps) {
    const {
        value,
        autoFocus,
        variant,
        multiline,
        rowsMax,
        onInputChange,
        onClick,
    } = props;
    const [input, setInput] = useState(value);
    
    const handleOnEmitValue = onEmitValue(onInputChange, input, value);
    const handleChange = onChange(setInput);
    const handleBlur = onBlur(handleOnEmitValue);
    const handleKeyPress = onKeyPress(Boolean(multiline));
    
    return (
        <TextInput
            value={input}
            onBlur={handleBlur}
            onChange={handleChange}
            onClick={onClick}
            onKeyPress={handleKeyPress}
            autoFocus={autoFocus}
            variant={variant}
            multiline={multiline}
            rowsMax={rowsMax}
        />
    );
}
